import type { FC } from 'react';
import * as React from 'react';
import {
  ModalInner,
  Stack,
  Typography,
  Button,
  Box,
  CountrySelector,
  FTextInput,
  IbanInput,
} from '@pflegenavi/web-components';
import { styled } from '@mui/system';
import { CircularProgress } from '@mui/material';
import type { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

interface BankAccountFormValues {
  firstName: string;
  lastName: string;
  iban: string;
  city: string;
  addressLine1: string;
  addressLine2?: string;
  country: string;
  postalCode: string;
}

export interface PaymentMethodSelectionModalViewProps {
  open: boolean;
  onClose: () => void;
  amount: number;
  existingMandateActive: boolean;
  isLoading?: boolean;
  isSubmitting: boolean;
  currentStep: 'select' | 'confirm' | 'new';
  paymentMethod: 'existing' | 'new';
  onPaymentMethodChange: (method: 'existing' | 'new') => void;
  onNextStep: () => Promise<void>;
  onBack: () => void;
  onFinalPayment: () => void;
  formik: FormikProps<BankAccountFormValues>;
  countryOptions: Array<{ label: string; value: string }>;
  onChangeCountry: (countryId: string) => void;
  onCountryBlur: () => void;
  pendingBankAccount: {
    iban?: string;
    isNewAccount: boolean;
  };
  maskIban: (iban?: string) => string;
  fCurrencyCents: (amount: number) => string;
}

export const PaymentMethodSelectionModalView: FC<
  PaymentMethodSelectionModalViewProps
> = ({
  currentStep,
  paymentMethod,
  onPaymentMethodChange,
  onNextStep,
  onBack,
  onFinalPayment,
  formik,
  countryOptions,
  onChangeCountry,
  onCountryBlur,
  pendingBankAccount,
  maskIban,
  fCurrencyCents,
  amount,
  existingMandateActive,
  isLoading = false,
  isSubmitting,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <ModalInner>
      <ModalContainer>
        {currentStep === 'select' && existingMandateActive && (
          <>
            <Typography variant="h5">
              {t('mangopay.direct-debit.payment-method-selection.title')}
            </Typography>
            <Typography variant="body2" color="grey.700" sx={{ mb: 3 }}>
              {t('mangopay.direct-debit.payment-method-selection.description')}
            </Typography>

            <Stack spacing={3}>
              <PaymentMethodOption
                selected={paymentMethod === 'existing'}
                onClick={() => onPaymentMethodChange('existing')}
              >
                <Typography variant="body1" fontWeight={500}>
                  {t(
                    'mangopay.direct-debit.payment-method-selection.use-existing-mandate'
                  )}
                </Typography>
                <Typography variant="body2" color="grey.700">
                  {t(
                    'mangopay.direct-debit.payment-method-selection.use-existing-mandate-description'
                  )}
                </Typography>
              </PaymentMethodOption>

              <PaymentMethodOption
                selected={paymentMethod === 'new'}
                onClick={() => onPaymentMethodChange('new')}
              >
                <Typography variant="body1" fontWeight={500}>
                  {t(
                    'mangopay.direct-debit.payment-method-selection.use-new-bank-account'
                  )}
                </Typography>
                <Typography variant="body2" color="grey.700">
                  {t(
                    'mangopay.direct-debit.payment-method-selection.use-new-bank-account-description'
                  )}
                </Typography>
              </PaymentMethodOption>

              {paymentMethod === 'new' && (
                <Box sx={{ mt: 2 }}>
                  <BankAccountForm
                    formik={formik}
                    countryOptions={countryOptions}
                    onChangeCountry={onChangeCountry}
                    onCountryBlur={onCountryBlur}
                  />
                </Box>
              )}
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              gap={2}
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isSubmitting || isLoading}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={onNextStep}
                disabled={isSubmitting || isLoading}
              >
                {t('actions.continue')}
              </Button>
            </Stack>
          </>
        )}

        {currentStep === 'new' && (
          <>
            <Typography variant="h5">
              {t(
                'mangopay.direct-debit.payment-method-selection.enter-bank-details'
              )}
            </Typography>
            <Typography variant="body2" color="grey.700" sx={{ mb: 3 }}>
              {t(
                'mangopay.direct-debit.payment-method-selection.bank-details-description'
              )}
            </Typography>

            <BankAccountForm
              formik={formik}
              countryOptions={countryOptions}
              onChangeCountry={onChangeCountry}
              onCountryBlur={onCountryBlur}
            />

            <Stack
              direction="row"
              justifyContent="flex-end"
              gap={2}
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isSubmitting || isLoading}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={onNextStep}
                disabled={isSubmitting || isLoading}
              >
                {t('actions.continue')}
              </Button>
            </Stack>
          </>
        )}

        {currentStep === 'confirm' && (
          <>
            <Typography variant="h5">
              {t('mangopay.direct-debit.payment-confirmation.title')}
            </Typography>

            <Box sx={{ my: 3, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={500}>
                    {t('mangopay.direct-debit.payment-confirmation.amount')}:
                  </Typography>
                  <Typography fontWeight={700}>
                    {fCurrencyCents(amount)}
                  </Typography>
                </Stack>

                {pendingBankAccount.iban && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontWeight={500}>
                      {t('mangopay.direct-debit.payment-confirmation.iban')}:
                    </Typography>
                    <Typography>
                      {maskIban(pendingBankAccount.iban || formik.values.iban)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            <Typography variant="body2" color="grey.700" sx={{ mb: 2 }}>
              {t('mangopay.direct-debit.payment-confirmation.processor')}
            </Typography>

            <Typography variant="body2" color="grey.700" sx={{ mb: 2 }}>
              {t('mangopay.direct-debit.payment-confirmation.processing-time')}
            </Typography>

            {pendingBankAccount.isNewAccount && (
              <Typography
                variant="body2"
                color="info.main"
                sx={{ mb: 3, fontWeight: 500 }}
              >
                {t('mangopay.direct-debit.payment-confirmation.redirect-info')}
              </Typography>
            )}

            <Stack
              direction="row"
              justifyContent="space-between"
              gap={2}
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                onClick={onBack}
                disabled={isSubmitting || isLoading}
              >
                {t('back')}
              </Button>
              <Stack direction="row" gap={2}>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={isSubmitting || isLoading}
                >
                  {t('actions.cancel')}
                </Button>
                <Button
                  variant="contained"
                  onClick={onFinalPayment}
                  disabled={isSubmitting || isLoading}
                  startIcon={
                    isSubmitting || isLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {t(
                    'mangopay.direct-debit.payment-confirmation.confirm-payment'
                  )}
                </Button>
              </Stack>
            </Stack>
          </>
        )}
      </ModalContainer>
    </ModalInner>
  );
};

const BankAccountForm: FC<{
  formik: FormikProps<BankAccountFormValues>;
  countryOptions: Array<{ label: string; value: string }>;
  onChangeCountry: (countryId: string) => void;
  onCountryBlur: () => void;
}> = ({ formik, countryOptions, onChangeCountry, onCountryBlur }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing={2}>
      <Stack direction="row" gap={2}>
        <FTextInput
          id="firstName"
          name="firstName"
          label={t('mangopay.direct-debit.setup.first-name')}
          placeholder={t('mangopay.direct-debit.setup.first-name')}
          fullWidth
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
        <FTextInput
          id="lastName"
          name="lastName"
          label={t('mangopay.direct-debit.setup.last-name')}
          placeholder={t('mangopay.direct-debit.setup.last-name')}
          fullWidth
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
      </Stack>
      <IbanInput
        value={formik.values.iban ?? ''}
        setValue={(newIban) => formik.setFieldValue('iban', newIban)}
        error={
          formik.touched.iban && formik.errors.iban
            ? String(formik.errors.iban)
            : undefined
        }
      />
      <Stack direction="row" gap={2}>
        <FTextInput
          id="addressLine1"
          name="addressLine1"
          label={t('mangopay.direct-debit.setup.address-line-1')}
          placeholder={t('mangopay.direct-debit.setup.address-line-1')}
          fullWidth
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
        <FTextInput
          id="addressLine2"
          name="addressLine2"
          label={t('mangopay.direct-debit.setup.address-line-2')}
          placeholder={t('mangopay.direct-debit.setup.address-line-2')}
          fullWidth
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
      </Stack>
      <Stack direction="row" gap={2}>
        <FTextInput
          id="city"
          name="city"
          label={t('mangopay.direct-debit.setup.city')}
          placeholder={t('mangopay.direct-debit.setup.city')}
          fullWidth
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
        <FTextInput
          id="postalCode"
          name="postalCode"
          label={t('mangopay.direct-debit.setup.postal-code')}
          placeholder={t('mangopay.direct-debit.setup.postal-code')}
          fullWidth
          formik={formik}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />
      </Stack>
      <CountrySelector
        name="country"
        label={t('mangopay.direct-debit.setup.country')}
        onChangeCountry={onChangeCountry}
        onBlur={onCountryBlur}
        countries={countryOptions}
        countryId={formik.values.country}
        errorMessage={
          formik.touched.country && formik.errors.country
            ? String(formik.errors.country)
            : undefined
        }
        fullWidth
      />
    </Stack>
  );
};

const ModalContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    width: 550,
    maxWidth: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
  })
);

const PaymentMethodOption = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) =>
  theme.unstable_sx({
    p: 2,
    border: selected ? '2px solid' : '1px solid',
    borderColor: selected ? 'primary.main' : 'grey.300',
    borderRadius: 2,
    cursor: 'pointer',
    bgcolor: selected ? 'primary.lighter' : 'background.paper',
    '&:hover': {
      borderColor: selected ? 'primary.main' : 'primary.light',
    },
  })
);
